/* eslint-disable @typescript-eslint/naming-convention */
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, catchError, map, of, retry, tap } from 'rxjs';
import { HRM_LOOKUP_API_SEGMENT } from 'src/app/config/constants/hrm-automotive-service.constants';
import { environment } from 'src/environments/environment';
import { ApiPagination } from 'src/app/widgets/xa-paginator/xa-paginator.component';

export interface AttendanceListResponse {
  value?: any[];
  '@odata.count'?: number;
  /** Alternative REST API response shape */
  data?: {
    data?: any[];
    totalCount?: number;
    page?: number;
    pageSize?: number;
  };
}

/** Item from GET `/api/Lookup/<segment>/AttendanceStatus` (see `HRM_LOOKUP_API_SEGMENT`). */
export interface AttendanceStatusLookupItem {
  id: number;
  code: string;
  name: string;
  description?: string;
  displayOrder?: number;
}

/** Item from GET `/api/Lookup/organization-locations` (attendance filter / LocationName). */
export interface OrganizationLocationLookupItem {
  orgId: number;
  name: string;
  location: string;
  locationId: number;
  /** Some responses use PascalCase. */
  Location?: string;
}

export interface AttendanceCalculationSettingsPayload {
  attendanceCalculationSettingsGuid: string;
  settingsVersion: number;
  checkInGracePeriodMinutes: number;
  checkOutGracePeriodMinutes: number;
  minimumHoursFullDay: number;
  minimumHoursHalfDay: number;
  weekendAttendanceTreatment: 'IGNORE' | 'OVERTIME' | 'COMP_OFF';
  holidayAttendanceTreatment: 'IGNORE' | 'OVERTIME' | 'COMP_OFF';
  allowNegativeAttendanceBalance: boolean;
  attendanceRoundingIncrementMinutes: number;
  effectiveFrom: string;
  defaultWorkingHoursPerDay: number;
}

/** Where the employee is right now, as distinct from the day's attendance status. */
export type HrmWorkStatus =
  | 'NOT_STARTED'
  | 'CHECKED_IN'
  | 'ON_BREAK'
  | 'CHECKED_OUT'
  | 'AUTO_CLOSED';

/** One check-in to check-out cycle. */
export type HrmAttendanceSession = {
  id: number;
  sequenceNumber: number;
  checkInTimeUtc: string;
  checkOutTimeUtc: string | null;
  /** Duration of this session; null while it is still open. */
  effectiveHours: number | null;
  /** Break that preceded this session; null for the first session of the day. */
  breakBeforeHours: number | null;
  isOpen: boolean;
  sessionState: string;
  closureReason: string | null;
};

export type HrmCheckInState = {
  isCheckedIn: boolean;
  /** Whether user has any check-in record for today (open or closed). */
  hasCheckedInToday: boolean;
  /** Server UTC time string of the day's FIRST check-in (null when none today). */
  checkInTimeUtc: string | null;
  /** Server UTC time string of the day's FINAL check-out; null while a session is open. */
  checkOutTimeUtc: string | null;
  /** Derived seconds elapsed in the CURRENT open session (0 when not checked in). */
  workSeconds: number;

  // --- Multi-session fields (US 95621). ---

  workStatus: HrmWorkStatus;
  /** Server-owned button visibility, so the client never has to infer whether a punch is legal. */
  canCheckIn: boolean;
  canCheckOut: boolean;
  /** Start of the open session; anchor for the running work timer. */
  currentSessionStartUtc: string | null;
  /** Last check-out; anchor for the running break timer while ON_BREAK. */
  lastCheckOutTimeUtc: string | null;
  /** Seconds already worked in COMPLETED sessions today (excludes the running one). */
  completedWorkSeconds: number;
  /**
   * Gross seconds banked so far: first check-in to the final COMPLETED check-out. Excludes any
   * running session, exactly like `completedWorkSeconds`.
   */
  completedGrossSeconds: number;
  /** Seconds of break already taken today (excludes the running break). */
  breakSeconds: number;
  sessionCount: number;
  effectiveHours: number;
  grossHours: number;
  breakHours: number;
  sessions: HrmAttendanceSession[];
  /**
   * Date.now() minus the server's clock, applied to all elapsed-time maths so a skewed device
   * clock cannot distort the timers.
   */
  serverClockSkewMs: number;
};

/** Attendance tabs: Me / My Team / All Employees. */
export type HrmAttendanceViewMode = 'me' | 'myteam' | 'all';

/** Tab to the server-side scope value it authorizes against. */
const SCOPE_BY_VIEW_MODE: Record<HrmAttendanceViewMode, string> = {
  me: 'me',
  myteam: 'myteam',
  all: 'allemployees',
};

/** Transient network failures on a punch are retried; the idempotency key makes that safe. */
const RETRY_COUNT = 2;
const RETRY_DELAY_MS = 800;

/** Shown where a punch was never made, as opposed to a day that is simply still running. */
export const MISSING_PUNCH_KEY = 'missing_punch';

const EMPTY_CHECK_IN_STATE: HrmCheckInState = {
  isCheckedIn: false,
  hasCheckedInToday: false,
  checkInTimeUtc: null,
  checkOutTimeUtc: null,
  workSeconds: 0,
  workStatus: 'NOT_STARTED',
  canCheckIn: true,
  canCheckOut: false,
  currentSessionStartUtc: null,
  lastCheckOutTimeUtc: null,
  completedWorkSeconds: 0,
  completedGrossSeconds: 0,
  breakSeconds: 0,
  sessionCount: 0,
  effectiveHours: 0,
  grossHours: 0,
  breakHours: 0,
  sessions: [],
  serverClockSkewMs: 0,
};

@Injectable({ providedIn: 'root' })
export class AttendanceService {
  private readonly http = inject(HttpClient);
  private readonly translate = inject(TranslateService);
  private readonly baseUrl = environment.hrmApiUrl;
  private readonly deviceId = 'WEB-APP';

  private readonly checkInStateSubject = new BehaviorSubject<HrmCheckInState>({ ...EMPTY_CHECK_IN_STATE });
  /** Shared check-in state for header + attendance page. */
  readonly checkInState$ = this.checkInStateSubject.asObservable();

  /**
   * Full clock state for a working day: current status, session breakdown and running totals.
   * This is what restores the UI after a refresh or a reconnect.
   */
  getAttendanceStatus(date?: string): Observable<any> {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    return this.http.get(`${this.baseUrl}/api/Attendance/status${query}`);
  }

  /** Refresh and broadcast the current clock state (used by header + attendance). */
  refreshCurrentCheckIn(): Observable<any> {
    return this.getAttendanceStatus().pipe(
      tap(res => this.setCheckInStateFromStatusResponse(res)),
    );
  }

  /** @internal Maps GET /api/Attendance/status onto the shared state. */
  private setCheckInStateFromStatusResponse(res: any): void {
    const data = res?.data;
    if (!data) {
      this.checkInStateSubject.next({ ...EMPTY_CHECK_IN_STATE });
      return;
    }

    // Trust the server's clock over the device's for every elapsed-time calculation.
    const serverTimeMs = Date.parse(data.serverTimeUtc ?? '');
    const serverClockSkewMs = Number.isNaN(serverTimeMs) ? 0 : Date.now() - serverTimeMs;

    const sessions: HrmAttendanceSession[] = Array.isArray(data.sessions)
      ? data.sessions.map((s: any) => ({
          id: s.id,
          sequenceNumber: s.sequenceNumber,
          checkInTimeUtc: s.checkInTimeUtc,
          checkOutTimeUtc: s.checkOutTimeUtc ?? null,
          effectiveHours: s.effectiveHours ?? null,
          breakBeforeHours: s.breakBeforeHours ?? null,
          isOpen: !!s.isOpen,
          sessionState: s.sessionState ?? 'SUCCESS',
          closureReason: s.closureReason ?? null,
        }))
      : [];

    const currentSessionStartUtc = data.currentSessionStartUtc ?? null;
    const workSeconds = currentSessionStartUtc
      ? this.elapsedSecondsSince(currentSessionStartUtc, serverClockSkewMs)
      : 0;

    this.checkInStateSubject.next({
      isCheckedIn: !!data.isCheckedIn,
      hasCheckedInToday: !!data.hasCheckedInToday,
      checkInTimeUtc: data.firstCheckInTimeUtc ?? null,
      checkOutTimeUtc: data.finalCheckOutTimeUtc ?? null,
      workSeconds,
      workStatus: (data.workStatus as HrmWorkStatus) ?? 'NOT_STARTED',
      canCheckIn: !!data.canCheckIn,
      canCheckOut: !!data.canCheckOut,
      currentSessionStartUtc,
      lastCheckOutTimeUtc: data.lastCheckOutTimeUtc ?? null,
      completedWorkSeconds: Math.round((data.effectiveHours ?? 0) * 3600),
      completedGrossSeconds: Math.round((data.grossHours ?? 0) * 3600),
      breakSeconds: Math.round((data.breakHours ?? 0) * 3600),
      sessionCount: data.sessionCount ?? sessions.length,
      effectiveHours: data.effectiveHours ?? 0,
      grossHours: data.grossHours ?? 0,
      breakHours: data.breakHours ?? 0,
      sessions,
      serverClockSkewMs,
    });
  }

  /** @internal Whole seconds elapsed since a UTC instant, corrected for device clock skew. */
  private elapsedSecondsSince(utc: string, serverClockSkewMs: number, nowMs: number = Date.now()): number {
    const startMs = Date.parse(utc);
    if (Number.isNaN(startMs)) {
      return 0;
    }
    return Math.max(0, Math.floor((nowMs - serverClockSkewMs - startMs) / 1000));
  }

  /**
   * Day's effective time as of `nowMs`: the seconds banked in completed sessions plus the session
   * currently running, if any.
   *
   * This is what both the header widget and the attendance page display. It covers every case the
   * acceptance criteria list without branching on them: no check-in yet gives 0; a single open
   * session gives check-in to now; several cycles with the last one open give the closed sessions
   * plus the running one; a fully closed day gives the banked total and stops moving.
   *
   * Derived from absolute timestamps on every call rather than accumulated tick by tick, so a
   * throttled background tab, a dropped interval or a destroyed-and-recreated view all self-correct
   * on the next read instead of drifting.
   */
  effectiveSecondsAt(state: HrmCheckInState | null | undefined, nowMs: number = Date.now()): number {
    const banked = Math.max(0, Math.round(state?.completedWorkSeconds ?? 0));
    return banked + this.openSessionSecondsAt(state, nowMs);
  }

  /**
   * Day's gross time as of `nowMs`: first check-in to now while a session is open, otherwise first
   * check-in to the final check-out.
   */
  grossSecondsAt(state: HrmCheckInState | null | undefined, nowMs: number = Date.now()): number {
    if (!state?.checkInTimeUtc) {
      return 0;
    }
    if (state.currentSessionStartUtc) {
      return this.elapsedSecondsSince(state.checkInTimeUtc, state.serverClockSkewMs ?? 0, nowMs);
    }
    return Math.max(0, Math.round(state.completedGrossSeconds ?? 0));
  }

  /** Seconds elapsed in the session currently running; 0 when none is open. */
  openSessionSecondsAt(state: HrmCheckInState | null | undefined, nowMs: number = Date.now()): number {
    if (!state?.currentSessionStartUtc) {
      return 0;
    }
    return this.elapsedSecondsSince(state.currentSessionStartUtc, state.serverClockSkewMs ?? 0, nowMs);
  }

  /**
   * `1h 18m 05s` - the single duration format shared by the header widget and the attendance page,
   * so the two can never disagree on how the same number reads.
   */
  /**
   * Decimal hours as `1h 18m`, for the attendance listing and the session breakdown.
   *
   * Display only: these values are never sorted or filtered client-side (the OData listing does
   * both server-side against the raw decimal columns), so formatting them here changes nothing but
   * how they read.
   */
  static formatHoursShort(value: number | string | null | undefined, fallback = '-'): string {
    const hours = typeof value === 'string' ? Number(value) : value;
    if (hours == null || typeof hours !== 'number' || Number.isNaN(hours)) {
      return fallback;
    }
    const totalMinutes = Math.round(Math.max(0, hours) * 60);
    return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
  }

  static formatDuration(totalSeconds: number): string {
    const safe = Math.max(0, Math.floor(totalSeconds || 0));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const seconds = safe % 60;
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }

  /**
   * Get attendance summary for summary cards.
   * date format: YYYY-MM-DD, scope: 'me' | 'myteam'
   * Returns { success, data: { totalEmployees, workingDays, present, late, absent, onLeave, unmarked } }
   */
  getAttendanceSummary(date: string, viewMode: HrmAttendanceViewMode): Observable<any> {
    const scope = SCOPE_BY_VIEW_MODE[viewMode] ?? 'me';
    const url = `${this.baseUrl}/api/Attendance/summary?date=${encodeURIComponent(date)}&scope=${scope}`;
    return this.http.get(url);
  }

  /**
   * Get attendance lookup data by type.
   * @param type - e.g. 'attendanceType'
   * Returns { success, data: [{ id, code, name, description }] }
   */
  getAttendanceLookup(type: string): Observable<any> {
    const url = `${this.baseUrl}/api/Attendance/lookup?type=${encodeURIComponent(type)}`;
    return this.http.get(url);
  }

  /**
   * HRMS lookup: attendance row status codes for filters (odata Status field).
   * Response: { success, data: [{ id, code, name, description, displayOrder }] }.
   */
  getAttendanceStatusLookup(): Observable<{
    success?: boolean;
    data?: AttendanceStatusLookupItem[];
    msg?: string;
    message?: string;
  }> {
    const headers = new HttpHeaders({
      accept: 'application/json;odata.metadata=minimal;odata.streaming=true',
    });
    const url = `${this.baseUrl}/api/Lookup/${HRM_LOOKUP_API_SEGMENT}/AttendanceStatus`;
    return this.http.get<{
      success?: boolean;
      data?: AttendanceStatusLookupItem[];
      msg?: string;
      message?: string;
    }>(url, { headers });
  }

  /**
   * Organization branch locations for attendance list filter (OData `LocationName`).
   * Response: { success, data: [{ orgId, name, location, locationId }] }; use `location` for display and filter.
   */
  getOrganizationLocations(): Observable<{
    success?: boolean;
    data?: OrganizationLocationLookupItem[];
    msg?: string;
    message?: string;
  }> {
    const headers = new HttpHeaders({
      accept: 'application/json;odata.metadata=minimal;odata.streaming=true',
    });
    const url = `${this.baseUrl}/api/Lookup/organization-locations`;
    return this.http.get<{
      success?: boolean;
      data?: OrganizationLocationLookupItem[];
      msg?: string;
      message?: string;
    }>(url, { headers });
  }

  /** Handles getAttendanceCalculationSettings. */
  getAttendanceCalculationSettings(): Observable<any> {
    const url = `${this.baseUrl}/api/AttendanceCalculationSettings`;
    return this.http.get(url);
  }

  /** Handles updateAttendanceCalculationSettings. */
  updateAttendanceCalculationSettings(
    payload: AttendanceCalculationSettingsPayload,
  ): Observable<any> {
    const idempotencyKey = this.generateIdempotencyKey();
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey,
      'Content-Type':
        'application/json;odata.metadata=minimal;odata.streaming=true',
    });
    const url = `${this.baseUrl}/api/AttendanceCalculationSettings`;
    return this.http.put(url, payload, { headers });
  }

  /**
   * Get attendance list with OData query support.
   */
  getAttendanceList(
    pagination: ApiPagination,
    filterQuery?: string,
    viewMode?: HrmAttendanceViewMode,
    searchTerm?: string,
    currentUserId?: string | number,
  ): Observable<AttendanceListResponse> {
    const isTeamView = viewMode === 'myteam' || viewMode === 'all';
    const top = isTeamView ? 10 : (pagination.pageSize ?? 20);
    const skip = ((pagination.currentPage ?? 1) - 1) * top;
    const orderBy = isTeamView
      ? 'AttendanceDate'
      : pagination.orderBy || 'EmployeeName';
    const sortDir = isTeamView ? 'desc' : pagination.sortDirection || 'desc';

    const params: string[] = [
      `$top=${top}`,
      `$skip=${skip}`,
      `$orderby=${orderBy} ${sortDir}`,
      '$count=true',
      // Scoping is server-side and role-checked. The previous client-side
      // `EmployeeId eq/ne <uid>` filters could not express "all employees", and the `ne` variant
      // wrongly excluded a manager's own row from their team view.
      `scope=${SCOPE_BY_VIEW_MODE[viewMode ?? 'me']}`,
    ];

    const filterParts: string[] = [];
    if (filterQuery?.trim()) {
      filterParts.push(`(${filterQuery})`);
    }
    if (searchTerm?.trim()) {
      const term = searchTerm.trim().replaceAll("'", "''");
      filterParts.push(`contains(EmployeeName,'${term}')`);
    }
    if (filterParts.length > 0) {
      params.push(`$filter=${filterParts.join(' and ')}`);
    }

    const url = `${this.baseUrl}/odata/attendance?${params.join('&')}`;
    return this.http.get<AttendanceListResponse>(url);
  }

  /**
   * Map OData attendance row to table format.
   */
  mapAttendanceRow(row: any): any {
    const checkInRaw = row.CheckInTimeUtc ?? row.checkInTimeUtc ?? row.checkIn;
    const checkOutRaw =
      row.CheckOutTimeUtc ?? row.checkOutTimeUtc ?? row.checkOut;
    const dateRaw =
      row.AttendanceDate ?? row.attendanceDate ?? checkInRaw ?? row.date;
    const regularizeRequestDate = this.toLocalCalendarDateForPicker(dateRaw);

    const attendanceId = row.Id ?? row.id ?? row.AttendanceId ?? '';

    // Minutes past the scheduled shift start; set by the backend only for LATE rows (US 98982).
    const lateByMinutes = row.LateByMinutes ?? row.lateByMinutes ?? null;

    return {
      id: attendanceId,
      /** Numeric record id, needed to load the session breakdown. */
      attendanceId,
      /**
       * Owning employee. Needed in the team scopes to tell the caller's own row apart from a
       * report's, because regularization is self-service (US 96147).
       */
      employeeId: row.EmployeeId ?? row.employeeId ?? null,
      employeeName:
        row.EmployeeName ??
        row.employeeName ??
        row.EmployeeCode ??
        row.employeeCode ??
        '',
      date: this.formatDate(dateRaw),
      /** Local midnight for the same calendar day as {@link formatDate}; use in regularize dialog (not DD/MM string). */
      regularizeRequestDate,
      location:
        row.LocationName ??
        row.Location ??
        row.locationName ??
        row.location ??
        '-',
      /** First Clock-In of the day; the delay for a LATE row is shown under the status badge. */
      checkIn: this.formatTime(checkInRaw),
      /** Raw minutes past shift start for a LATE row, if any consumer needs it unformatted. */
      lateByMinutes,
      /**
       * Final Clock-Out of the day. A day that has ended with no check-out recorded reads as
       * MISSING rather than as a dash: the punch was never made, and the auto-close no longer
       * invents one to fill the gap (US 95621).
       */
      checkOut: this.formatPunchOut(checkOutRaw, row),
      effectiveHours: AttendanceService.formatHoursShort(row.EffectiveHours ?? row.effectiveHours),
      grossHours: AttendanceService.formatHoursShort(row.GrossHours ?? row.grossHours),
      breakHours: AttendanceService.formatHoursShort(row.BreakHours ?? row.breakHours),
      sessionCount: row.SessionCount ?? row.sessionCount ?? 0,
      hasOpenSession: row.HasOpenSession ?? row.hasOpenSession ?? false,
      needsHrReview: row.NeedsHrReview ?? row.needsHrReview ?? false,
      status: this.formatStatus(row.Status ?? row.status ?? '', lateByMinutes),
    };
  }

  /**
   * Record clock-in for logged-in user.
   */
  checkIn(): Observable<any> {
    // The key is generated ONCE, outside the request, and captured by the closure. `retry` then
    // re-sends the SAME Idempotency-Key, which is exactly what makes recovery from a dropped
    // connection safe: the server replays the original response instead of opening a second
    // session. Do not move this inside the request factory.
    const idempotencyKey = this.generateIdempotencyKey();
    const url = `${this.baseUrl}/api/attendance/clock-in`;
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey,
      'Content-Type':
        'application/json; odata.metadata=minimal; odata.streaming=true',
    });
    const body = { idempotencyKey, deviceId: this.deviceId };
    return this.http.post(url, body, { headers }).pipe(
      retry({ count: RETRY_COUNT, delay: RETRY_DELAY_MS }),
      tap({
        next: () => {
          // Ensure both header + attendance update from a single source of truth.
          this.refreshCurrentCheckIn().subscribe();
        }
      }),
    );
  }

  /**
   * Record clock-out for logged-in user.
   */
  checkOut(): Observable<any> {
    const idempotencyKey = this.generateIdempotencyKey();
    const url = `${this.baseUrl}/api/attendance/clock-out`;
    const headers = new HttpHeaders({
      'Idempotency-Key': idempotencyKey,
      'Content-Type':
        'application/json; odata.metadata=minimal; odata.streaming=true',
    });
    const body = { idempotencyKey, deviceId: this.deviceId };
    return this.http.post(url, body, { headers }).pipe(
      retry({ count: RETRY_COUNT, delay: RETRY_DELAY_MS }),
      tap({
        next: () => {
          // Re-read from the server rather than assuming a cleared state. A mid-day check-out
          // leaves real running totals (effective, gross, break, session count) that the previous
          // hardcoded reset discarded, and the day may still continue with another check-in.
          this.refreshCurrentCheckIn().subscribe();
        }
      }),
    );
  }

  /** Session breakdown for one attendance record: every check-in/check-out cycle of that day. */
  getAttendanceSessions(attendanceId: number | string): Observable<any> {
    return this.http.get(`${this.baseUrl}/api/Attendance/${attendanceId}/sessions`);
  }

  /**
   * Submit attendance regularization request with optional document.
   * POST multipart/form-data: Request (JSON string), Document (File, optional).
   */
  submitRegularization(payload: {
    attendanceDate: Date;
    proposedCheckInUtc: Date;
    proposedCheckOutUtc: Date;
    regularizationType: string;
    attendanceType: string;
    reason: string;
    document?: File | null;
  }): Observable<any> {
    const idempotencyKey = this.generateIdempotencyKey();
    const formData = new FormData();
    const request = {
      idempotencyKey,
      attendanceDate: payload.attendanceDate.toISOString(),
      proposedCheckInUtc: payload.proposedCheckInUtc.toISOString(),
      proposedCheckOutUtc: payload.proposedCheckOutUtc.toISOString(),
      regularizationType: payload.regularizationType,
      attendanceType: payload.attendanceType,
      reason: payload.reason,
    };
    formData.append('Request', JSON.stringify(request));
    if (payload.document) {
      formData.append('Document', payload.document);
    }
    const headers = new HttpHeaders({ 'Idempotency-Key': idempotencyKey });
    const url = `${this.baseUrl}/api/attendance-regularizations/document`;
    return this.http.post(url, formData, { headers });
  }

  /** Handles generateIdempotencyKey. */
  private generateIdempotencyKey(): string {
    const rand = Math.random().toString(36).slice(2, 10);
    return `req-${Date.now()}-${rand}`;
  }

  /**
   * Local calendar date at 00:00:00 for date pickers (matches {@link formatDate} day, avoids parsing DD/MM strings).
   */
  private toLocalCalendarDateForPicker(dateRaw: any): Date | undefined {
    if (dateRaw == null || dateRaw === '') {
      return undefined;
    }
    const d = new Date(dateRaw);
    if (Number.isNaN(d.getTime())) {
      return undefined;
    }
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }

  /** Formats a date for HRMS attendance UI as DD/MM/YYYY (local calendar date). */
  private formatDate(val: any): string {
    if (!val) return '-';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  /**
   * The day's final clock-out.
   *
   * Three distinct cases the UI has to tell apart:
   *  - a recorded punch      -> the time
   *  - a session still open  -> a dash; the day simply is not finished
   *  - the day ended with no check-out -> MISSING, because the punch was never made
   */
  private formatPunchOut(checkOutRaw: any, row: any): string {
    if (checkOutRaw) {
      return this.formatTime(checkOutRaw);
    }
    const hasOpenSession = row?.HasOpenSession ?? row?.hasOpenSession ?? false;
    const hasCheckIn = !!(row?.CheckInTimeUtc ?? row?.checkInTimeUtc ?? row?.checkIn);
    return !hasOpenSession && hasCheckIn ? this.translate.instant(MISSING_PUNCH_KEY) : '-';
  }

  /** Formats a minute count as HH:mm:ss (e.g. 225 → "03:45:00"); the feed has minute precision. */
  private formatMinutesAsDuration(totalMinutes: number): string {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${pad(Math.floor(totalMinutes / 60))}:${pad(totalMinutes % 60)}:00`;
  }

  /** Handles formatTime. */
  private formatTime(val: any): string {
    if (!val) return '-';
    const d = new Date(val);
    return Number.isNaN(d.getTime())
      ? String(val)
      : d.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
  }

  /**
   * Normalises the derived status (US 98982) to a canonical i18n key + CSS class for the table's
   * status widget, which uses value.split('$#$')[0] as BOTH the translate key and the badge class.
   * The OData feed sends the display Name ("On Time") or the code ("ON_TIME"); both normalise to the
   * snake key "on_time", matching the i18n keys (on_time, late, not_logged, leave, half_day,
   * short_leave, off_day).
   */
  private formatStatus(val: string, lateByMinutes: number | null = null): string {
    if (!val) return 'not_logged$#$-';
    if (val.includes('$#$')) return val;
    const key = val.trim().replace(/\s+/g, '_').toLowerCase();
    // The widget renders the part after $#$ under the badge; a LATE row shows its delay there.
    const detail = lateByMinutes != null && lateByMinutes > 0 ? this.formatMinutesAsDuration(lateByMinutes) : '-';
    return `${key}$#$${detail}`;
  }
}
